export type ComparableDate = Date | string | number | null | undefined;

export type ImmunizationOccurrenceSummary<T> = {
  name: string;
  count: number;
  latestDateValue: number | null;
  firstIndex: number;
  records: T[];
};

type DateSortOptions<T> = {
  fallbackComparator?: (a: T, b: T) => number;
};

export function getComparableDateValue(date: ComparableDate): number | null {
  if (date === null || date === undefined || date === '') {
    return null;
  }

  if (date instanceof Date) {
    const time = date.getTime();
    return Number.isFinite(time) ? time : null;
  }

  if (typeof date === 'number') {
    return Number.isFinite(date) ? date : null;
  }

  const time = Date.parse(date);
  return Number.isFinite(time) ? time : null;
}

export function sortRecordsByDateDesc<T>(
  records: readonly T[],
  getDate: (record: T) => ComparableDate,
  options: DateSortOptions<T> = {}
): T[] {
  return records
    .map((record, index) => ({
      record,
      index,
      dateValue: getComparableDateValue(getDate(record)),
    }))
    .sort((a, b) => {
      if (a.dateValue !== null && b.dateValue !== null) {
        if (a.dateValue !== b.dateValue) {
          return b.dateValue - a.dateValue;
        }
      } else if (a.dateValue !== null) {
        return -1;
      } else if (b.dateValue !== null) {
        return 1;
      }

      const fallback = options.fallbackComparator?.(a.record, b.record) ?? 0;
      return fallback || a.index - b.index;
    })
    .map(({ record }) => record);
}

export function buildOccurrenceCounts<T>(
  records: readonly T[],
  getName: (record: T) => string | null | undefined
): Map<string, number> {
  const counts = new Map<string, number>();

  records.forEach((record) => {
    const name = getName(record);
    if (!name) {
      return;
    }

    counts.set(name, (counts.get(name) ?? 0) + 1);
  });

  return counts;
}

export function buildImmunizationOccurrenceSummaries<T>(
  records: readonly T[],
  getName: (record: T) => string | null | undefined,
  getDate: (record: T) => ComparableDate
): ImmunizationOccurrenceSummary<T>[] {
  const summaries = new Map<string, ImmunizationOccurrenceSummary<T>>();

  records.forEach((record, index) => {
    const name = getName(record);
    if (!name) {
      return;
    }

    const dateValue = getComparableDateValue(getDate(record));
    const summary = summaries.get(name);

    if (!summary) {
      summaries.set(name, {
        name,
        count: 1,
        latestDateValue: dateValue,
        firstIndex: index,
        records: [record],
      });
      return;
    }

    summary.count += 1;
    summary.records.push(record);
    if (
      dateValue !== null &&
      (summary.latestDateValue === null || dateValue > summary.latestDateValue)
    ) {
      summary.latestDateValue = dateValue;
    }
  });

  return sortRecordsByDateDesc(
    Array.from(summaries.values()),
    ({ latestDateValue }) => latestDateValue
  );
}

export function getStackedImmunizationLabels<T>(
  records: readonly T[],
  getName: (record: T) => string | null | undefined,
  getDate: (record: T) => ComparableDate
): string[] {
  return buildImmunizationOccurrenceSummaries(records, getName, getDate).map(
    ({ count, name }) => (count > 1 ? `${count}x ${name}` : name)
  );
}
